import { Tab, Tabs } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import Popup from "reactjs-popup";

import { useStateValue } from "../../../config/AppContext";
import { ExerciseTypesEnum } from "../../../types/StaticTypes";
import Icons from "../../Utilities/Icons";
import SearchFilter from "../../Utilities/SearchFilter";

const useStyles = makeStyles((theme) => ({
  mainContent: {
    display: "flex",
    alignItems: "center",
    width: window.innerWidth > 700 ? "97%" : "100%",
  },
  mainPopupTrigger: {
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    justifyContent: "center",
    padding: "18px 15px",
    backgroundColor: "#fffff",
    borderRadius: "5px",
    color: "#3e7fe9",
    cursor: "pointer",
    border: "1px solid",
    height: 38,
    zIndex: 2,
    fontSize: 15,
    whiteSpace: "nowrap",
    fontWeight: "bold",
  },
  mainPopupSpan: {
    textTransform: "capitalize",
    paddingLeft: 5,
    paddingRight: 5,
  },
  MUTab: {
    justifyContent: "unset",
    margin: window.innerWidth > 700 ? "1rem 1rem 0 0" : "1rem 0",
    border: 0,
    color: "white",
  },
  exerciseTypeListItem: {
    cursor: "pointer",
    borderRadius: "5px",
    textAlign: "left",
    paddingTop: 1,
    paddingBottom: 1,
    color: "var(--black)",
    "&:hover": {
      color: "var(--primary)",
    },
  },
  execiseTypeListItemActive: {
    color: "var(--primary)",
  },
  MUTabNotActive: {
    width: "50%",
    border: "1px solid #3E7FE9",
    textAlign: "center",
    color: "#3E7FE9",
    backgroundColor: "#ffff",
    cursor: "pointer",
    fontWeight: "bold",
    textTransform: "none",
  },
  MUTabActive: {
    width: "50%",
    textAlign: "center",
    backgroundColor: "#3E7FE9",
    cursor: "pointer",
    fontWeight: "bold",
    textTransform: "none",
  },
  MUTabLeft: {
    borderRadius: "5px 0 0 5px",
  },
  MUTabRight: {
    borderRadius: "0 5px 5px 0",
  },
}));

const customStyles: any = {
  mainPopupContent: {
    padding: "18px 15px",
    textAlign: "center",
    border: "none",
    borderRadius: "5px",
    zIndex: 3,
  },
};

export enum FilterTabs {
  "ALL_EXERCISES",
  "MY_EXERCISES",
}

export type FilterValue = {
  searchQuery: string;
  tab: FilterTabs;
  exerciseType: ExerciseTypesEnum | null;
};

interface ExerciseFilterProps {
  filterValue: FilterValue;
  onChange: (filterValue: FilterValue) => void;
}

const ExerciseFilter: React.FC<ExerciseFilterProps> = ({ filterValue, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { ic_filter } = Icons;
  const { catalog } = useStateValue();

  const isTabActive = (tab: FilterTabs) => filterValue.tab === tab;
  const isExerciseTypeActive = (exerciseType: FilterValue["exerciseType"]) =>
    filterValue.exerciseType === exerciseType;

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...filterValue,
      searchQuery: e.currentTarget.value,
    });
  };

  const handleExerciseTypeChange = (exerciseType: FilterValue["exerciseType"]) => {
    onChange({
      ...filterValue,
      exerciseType,
    });
  };

  const handleTabChange = (event: React.ChangeEvent<{}>, tab: FilterTabs) => {
    onChange({
      ...filterValue,
      tab,
    });
  };

  const generateTypeList = (close: () => void) => {
    let Types: React.ReactElement[] = [];
    Types = [
      ...Types,
      <li
        key={7}
        onClick={() => {
          handleExerciseTypeChange(null);
          close();
        }}
        className={clsx(classes.exerciseTypeListItem, {
          [classes.execiseTypeListItemActive]: isExerciseTypeActive(null),
        })}
      >
        {t("All")}
      </li>,
    ];
    for (let i = 0; i < 6; i++) {
      Types = [
        ...Types,
        <li
          key={i}
          onClick={() => {
            handleExerciseTypeChange(i);
            close();
          }}
          className={clsx(classes.exerciseTypeListItem, {
            [classes.execiseTypeListItemActive]: isExerciseTypeActive(i),
          })}
        >
          {catalog && catalog.typesDisplay(ExerciseTypesEnum[i]).toLowerCase()}
        </li>,
      ];
    }

    return (
      <div>
        <ul style={{ textTransform: "capitalize" }} aria-label="Exercise type filter list">
          {Types}
        </ul>
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          flexDirection:
            /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
            !/exercises/.test(window.location.href)
              ? "column"
              : "initial",
        }}
        className={classes.mainContent}
      >
        <SearchFilter placeholder="Exercise" onChange={handleSearchInputChange} />
        <Popup
          arrow
          closeOnDocumentClick
          position="bottom center"
          trigger={
            <div
              aria-label="Filter by exercise type"
              style={{
                width:
                  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
                  !/exercises/.test(window.location.href)
                    ? "100%"
                    : "fit-content",
                margin:
                  /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
                  !/exercises/.test(window.location.href)
                    ? "1rem 0 0 0"
                    : "20px 0 0 22px",
              }}
              className={classes.mainPopupTrigger}
            >
              <img
                alt="filter"
                src={ic_filter}
                style={{
                  width: "16px",
                  height: "16px",
                  // margin: '6px 5px auto 5px'
                  // marginInlineStart: '-20px',
                }}
              />
              {t("Filter by")} :{" "}
              <span className={classes.mainPopupSpan}>
                {filterValue.exerciseType
                  ? catalog.typesDisplay(ExerciseTypesEnum[filterValue.exerciseType]).toLowerCase()
                  : t("All")}
              </span>
            </div>
          }
          contentStyle={customStyles.mainPopupContent}
        >
          {(close) => generateTypeList(close)}
        </Popup>
      </div>
      <Tabs
        value={filterValue.tab}
        onChange={handleTabChange}
        variant="fullWidth"
        className={classes.MUTab}
        TabIndicatorProps={{
          style: {
            background: "transparent",
          },
        }}
      >
        <Tab
          id="simple-tab-0"
          aria-controls="simple-tabpanel-0"
          label={t("All Exercises")}
          className={clsx("p-2", classes.MUTabLeft, {
            [classes.MUTabActive]: isTabActive(FilterTabs.ALL_EXERCISES),
            [classes.MUTabNotActive]: !isTabActive(FilterTabs.ALL_EXERCISES),
          })}
        />
        <Tab
          id="simple-tab-1"
          aria-controls="simple-tabpanel-1"
          label={t("My Exercises")}
          className={clsx("p-2", classes.MUTabRight, {
            [classes.MUTabActive]: isTabActive(FilterTabs.MY_EXERCISES),
            [classes.MUTabNotActive]: !isTabActive(FilterTabs.MY_EXERCISES),
          })}
        />
      </Tabs>
    </>
  );
};

export default ExerciseFilter;
